import { zodResolver } from "@hookform/resolvers/zod";
import type { ConstructionSite } from "@msuite/katana";
import { moment } from "@msuite/katana";
import {
	Button,
	ButtonGroup,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	toast,
} from "@msuite/picasso";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ax } from "@/core";
import {
	AddPlanningRequestForm,
	type AddPlanningRequestFormType,
	addPlanningRequestSchema,
} from "@/forms/add-planning-request-form";
import { ConstructionSitePicker } from "./construction-site-picker";

/** Props Interface */
interface AddPlanningRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	constructionSiteId?: string;
}

/** Response Interface */
interface IGetPlanRequestDefaultsApiResponse {
	success: boolean;
	address: string;
	auftraggeber: string;
	projectNumber: string;
	projectName: string;
}

function getEmptyDefaultValues(): AddPlanningRequestFormType {
	return {
		dueAt: moment().add(1, "week").format("YYYY-MM-DD"),
		address: "",
		auftraggeber: "",
		projectNumber: "",
		projectName: "",
		statik: false,
		planunterlagen: false,
		anforderungen: "",
		sonstiges: "",
	};
}

async function fetchDefaultValues(constructionSiteId: string): Promise<AddPlanningRequestFormType> {
	const { data } = await ax.get<IGetPlanRequestDefaultsApiResponse>(
		`/v2/construction-sites/${constructionSiteId}/plan-request`,
	);
	return {
		...getEmptyDefaultValues(),
		address: data.address,
		auftraggeber: data.auftraggeber,
		projectNumber: data.projectNumber,
		projectName: data.projectName,
	};
}

export function AddPlanningRequestModal({
	isOpen,
	onClose,
	constructionSiteId: constructionSiteIdProp,
}: AddPlanningRequestModalProps) {
	/** State */
	const [selectedConstructionSiteId, setSelectedConstructionSiteId] = useState<string | undefined>(
		undefined,
	);

	/** Hooks */
	const constructionSiteId = constructionSiteIdProp ?? selectedConstructionSiteId;
	const methods = useForm<AddPlanningRequestFormType>({
		defaultValues: async () => {
			if (!constructionSiteId) return getEmptyDefaultValues();
			return fetchDefaultValues(constructionSiteId);
		},
		resolver: zodResolver(addPlanningRequestSchema),
	});

	/** Functions */
	async function handleSelectConstructionSite(constructionSite: ConstructionSite) {
		try {
			if (!constructionSite.id) return;
			setSelectedConstructionSiteId(constructionSite.id);
			methods.reset(await fetchDefaultValues(constructionSite.id));
		} catch (error) {
			toast.error("Es ist ein Fehler aufgetreten");
			console.error(error);
		}
	}

	async function handleOnSubmit(values: AddPlanningRequestFormType) {
		try {
			if (!constructionSiteId) throw new Error("No constructionSiteId provided");
			await ax.post(`/v2/construction-sites/${constructionSiteId}/plan-request`, {
				data: { values },
			});
			toast.success("Erfolgreich übermittelt");
			handleOnClose();
		} catch (error) {
			toast.error("Es ist ein Fehler aufgetreten");
			console.error(error);
		}
	}

	function handleOnClose() {
		methods.reset();
		setSelectedConstructionSiteId(undefined);
		onClose();
	}

	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
			>
				<ModalOverlay />
				<form onSubmit={methods.handleSubmit(handleOnSubmit)}>
					<ModalContent
						maxW="40rem"
						minH="45vh"
						maxH="75vh"
					>
						<ModalCloseButton />
						<ModalHeader>Planungsanfrage</ModalHeader>
						<ModalBody>
							{!methods.formState.isLoading &&
								(constructionSiteId ? (
									<AddPlanningRequestForm />
								) : (
									<ConstructionSitePicker onSelect={handleSelectConstructionSite} />
								))}
						</ModalBody>
						<ModalFooter>
							<ButtonGroup>
								<Button
									variant="ghost"
									onClick={handleOnClose}
								>
									abbrechen
								</Button>
								{constructionSiteId && (
									<Button
										type="submit"
										isLoading={methods.formState.isSubmitting}
									>
										Absenden
									</Button>
								)}
							</ButtonGroup>
						</ModalFooter>
					</ModalContent>
				</form>
			</Modal>
		</FormProvider>
	);
}
